import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Download, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TermoRendered } from '@/components/TermoRendered';

// Viewer público (read-only) do termo de adesão assinado.
// Rota: /termo/:adhesionId?t=<termo_view_token>
// Lê via endpoint getAdhesionTermo (adhesions não é lível público direto).
// "Baixar PDF" = window.print() com CSS de impressão (@media print em index.css).

const TERMO_API =
  import.meta.env.VITE_TERMO_API_URL ||
  'https://us-central1-sistemas-internos-dgenny.cloudfunctions.net/getAdhesionTermo';

interface TermoData {
  company_name: string;
  termo_version: string;
  accepted_at_client_iso: string;
  document_hash: string;
  document_snapshot: string;
}

export default function TermoView() {
  const { adhesionId } = useParams<{ adhesionId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [termo, setTermo] = useState<TermoData | null>(null);

  useEffect(() => {
    if (!adhesionId || !token) {
      setError('Link inválido. Solicite o link do termo ao seu contato na dgenny.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const url = `${TERMO_API}?id=${encodeURIComponent(adhesionId)}&t=${encodeURIComponent(token)}`;
        const resp = await fetch(url);
        if (resp.status === 403 || resp.status === 404) {
          setError('Link inválido ou expirado. Solicite um novo ao seu contato na dgenny.');
          return;
        }
        if (!resp.ok) {
          setError('Não foi possível carregar o termo. Tente novamente em instantes.');
          return;
        }
        const data = (await resp.json()) as TermoData;
        if (!data.document_snapshot) {
          setError('Termo indisponível. Fale com compre.ai@dgenny.com.br.');
          return;
        }
        setTermo(data);
      } catch {
        setError('Não foi possível carregar o termo. Verifique sua conexão e recarregue a página.');
      } finally {
        setLoading(false);
      }
    })();
  }, [adhesionId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando termo…
        </div>
      </div>
    );
  }

  if (error || !termo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <p className="text-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const aceiteData = termo.accepted_at_client_iso
    ? new Date(termo.accepted_at_client_iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    : '';

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Barra de ações — some na impressão */}
      <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">
              Termo de adesão — {termo.company_name}
            </span>
          </div>
          <Button size="sm" onClick={() => window.print()}>
            <Download className="h-4 w-4 mr-2" />
            Baixar PDF
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-background rounded-lg border shadow-sm p-6 sm:p-10 print:border-0 print:shadow-none print:p-0">
          <div className="text-xs text-muted-foreground mb-6 print:mb-4">
            {aceiteData && <>Aceito em {aceiteData} · </>}
            versão {termo.termo_version} · documento {termo.document_hash.slice(0, 12)}…
          </div>
          <article className="text-sm leading-relaxed text-foreground">
            <TermoRendered markdown={termo.document_snapshot} />
          </article>
        </div>
      </div>
    </div>
  );
}
